import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/terms')({
  component: TermsOfUse,
});

function TermsOfUse() {
  return (
    <div style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Terms of Use</h1>
          <p style={metaStyle}>Effective date: February 19, 2026</p>
        </header>

        <p>
          Welcome to Interlucent. These Terms of Use ("Terms") govern your access to and use of the
          Interlucent platform, including the console, APIs, streaming infrastructure, and any related
          services (collectively, the "Service"), operated by Interlucent ("we", "us", or "our").
        </p>
        <p>
          By creating an account or using the Service, you agree to be bound by these Terms. If you are
          using the Service on behalf of an organization, you represent that you have the authority to
          bind that organization.
        </p>
        <p>
          If you or your organization have entered into a Master Service Agreement ("MSA") or other
          written agreement with Interlucent, that agreement governs your use of the Service and takes
          precedence over these Terms to the extent of any conflict. These Terms apply to all matters
          not addressed by your MSA.
        </p>

        <h2 style={h2Style}>1. Eligibility</h2>
        <p>
          You must be at least 18 years old and capable of forming a binding contract to use the Service.
          Accounts registered by automated means (bots) are not permitted.
        </p>

        <h2 style={h2Style}>2. Your Account</h2>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all
          activity that occurs under your account. You agree to notify us immediately of any unauthorized
          use. We reserve the right to suspend or terminate accounts that violate these Terms.
        </p>

        <h2 style={h2Style}>3. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul style={listStyle}>
          <li>Use the Service for any unlawful purpose or in violation of any applicable law or regulation;</li>
          <li>Upload, stream, or distribute content that is infringing, defamatory, obscene, or harmful;</li>
          <li>Attempt to gain unauthorized access to any part of the Service or its infrastructure;</li>
          <li>Interfere with or disrupt the integrity or performance of the Service;</li>
          <li>Reverse-engineer, decompile, or disassemble any part of the Service;</li>
          <li>Use the Service to mine cryptocurrency, run denial-of-service attacks, or host malware;</li>
          <li>Resell, sublicense, or redistribute access to the Service without our prior written consent;</li>
          <li>Circumvent any usage limits, rate limits, or access controls.</li>
        </ul>

        <h2 style={h2Style}>4. Your Content</h2>
        <p>
          You retain ownership of the applications, assets, and data you upload to the Service ("Your
          Content"). By uploading Your Content, you grant us a limited, non-exclusive license to host,
          store, transmit, and stream Your Content solely to provide the Service to you. We do not claim
          ownership of Your Content and will not use it for any other purpose.
        </p>

        <h2 style={h2Style}>5. Service Availability</h2>
        <p>
          We strive to maintain high availability but do not guarantee uninterrupted access. The Service
          is provided on an "as-is" and "as-available" basis. We may perform maintenance, updates, or
          modifications that temporarily affect availability. We will make reasonable efforts to provide
          advance notice of planned downtime.
        </p>

        <h2 style={h2Style}>6. Intellectual Property</h2>
        <p>
          The Service, including its software, design, documentation, and branding, is owned by Interlucent
          and protected by intellectual property laws. These Terms do not grant you any right to use our
          trademarks, logos, or brand features without prior written consent.
        </p>

        <h2 style={h2Style}>7. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Interlucent and its officers, directors, employees, and
          agents shall not be liable for any indirect, incidental, special, consequential, or punitive
          damages, or any loss of profits, revenue, data, or business opportunities, arising out of or
          related to your use of the Service, regardless of the theory of liability. Our total aggregate
          liability shall not exceed the greater of (a) the amounts you paid to us in the twelve (12) months
          preceding the claim, or (b) one hundred U.S. dollars (US$100). Any claim arising under these
          Terms must be brought within twelve (12) months after the cause of action accrues, or it is
          permanently barred.
        </p>

        <h2 style={h2Style}>8. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Interlucent from and against any claims,
          liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of
          or related to your use of the Service, your violation of these Terms, or your violation of any
          rights of a third party. We reserve the right, at our own expense, to assume the exclusive
          defense and control of any matter subject to indemnification by you, and you agree to cooperate
          with our defense. You may not settle any claim without our prior written consent.
        </p>

        <h2 style={h2Style}>9. Termination</h2>
        <p>
          Either party may terminate this agreement at any time. You may delete your account through the
          console or by contacting us. We may suspend or terminate your access if you breach these Terms,
          with or without notice. Upon termination, your right to use the Service ceases immediately. We
          may retain Your Content for a reasonable period to allow retrieval, after which it may be deleted.
          Sections 4, 6, 7, 8, 10, and 12 survive termination.
        </p>

        <h2 style={h2Style}>10. Privacy</h2>
        <p>
          Your use of the Service is also governed by our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Privacy Policy</a>.
          By using the Service, you consent to the collection and use of information as described therein.
        </p>

        <h2 style={h2Style}>11. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. If we make material changes, we will notify you via
          the email associated with your account or through a notice in the Service. Your continued use of
          the Service after the effective date of any changes constitutes acceptance of the updated Terms.
        </p>

        <h2 style={h2Style}>12. Governing Law</h2>
        <p>
          These Terms are governed by and construed in accordance with applicable law, without regard to
          conflict-of-law principles. Any disputes arising under these Terms shall be resolved in the
          courts of competent jurisdiction.
        </p>

        <h2 style={h2Style}>13. Contact</h2>
        <p>
          If you have questions about these Terms, please contact us at{' '}
          <a href="mailto:legal@interlucent.ai" style={linkStyle}>legal@interlucent.ai</a>.
        </p>
      </article>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#fafafa',
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 24px',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
};

const articleStyle: React.CSSProperties = {
  maxWidth: '720px',
  width: '100%',
  color: '#1a202c',
  fontSize: '15px',
  lineHeight: '1.7',
};

const h1Style: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 700,
  margin: '0 0 4px 0',
  letterSpacing: '-0.02em',
};

const metaStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#718096',
  margin: '0 0 32px 0',
};

const h2Style: React.CSSProperties = {
  fontSize: '17px',
  fontWeight: 600,
  margin: '28px 0 8px 0',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '24px',
  margin: '8px 0',
};

const linkStyle: React.CSSProperties = {
  color: '#667eea',
  textDecoration: 'none',
};
